/**
 * Client-side visitor classification for analytics hygiene.
 *
 * Purpose: our first-party event tables (signup_funnel_events, app_events) are
 * only written by clients that execute JavaScript, which already excludes plain
 * HTTP crawlers. What still gets in is headless-browser traffic, link-unfurl
 * previewers, and vulnerability scanners driving a real engine. This module
 * labels those sessions so reports can separate humans from machines.
 *
 * This is NOT fingerprinting: no canvas/font/audio probing, no hashing, no
 * cross-site identifiers, no third-party calls. Only coarse, already-public
 * environment flags, evaluated once per session and cached.
 */

export type BotConfidence = "certain" | "suspected" | "none";

export type VisitorVerdict = {
  is_bot: boolean;
  bot_reason: string | null;
  bot_confidence: BotConfidence;
};

const CACHE_KEY = "mr_visitor_class";

/** Substrings that appear only in automated agents. */
const UA_BOT_PATTERNS: { re: RegExp; reason: string }[] = [
  { re: /headlesschrome|headless/i, reason: "headless_browser" },
  { re: /puppeteer|playwright|selenium|phantomjs|cypress|webdriver/i, reason: "automation_framework" },
  { re: /\bbot\b|bot\/|robot|crawler|crawl|spider|slurp/i, reason: "crawler_ua" },
  { re: /scanner|nuclei|nikto|zgrab|masscan|censys|shodan|paloalto|expanse/i, reason: "scanner_ua" },
  { re: /python-requests|python-urllib|aiohttp|httpx|go-http-client|java\/|okhttp|libwww|lwp::|curl\/|wget\//i, reason: "http_client_ua" },
  { re: /facebookexternalhit|slackbot|twitterbot|discordbot|telegrambot|whatsapp|linkedinbot|embedly|preview/i, reason: "link_preview" },
  { re: /lighthouse|pagespeed|gtmetrix|pingdom|uptime|monitor/i, reason: "monitoring_tool" },
];

/**
 * Paths nothing on this site links to. Requests for them come from scanners
 * probing for common CMS/config endpoints or mass-crawling legal pages.
 */
const SCANNER_PATH_PATTERNS: RegExp[] = [
  /^\/wp-/i,
  /^\/wordpress/i,
  /\/\.env$/i,
  /\/\.git/i,
  /^\/(?:admin|administrator)\.php/i,
  /^\/(?:phpmyadmin|cgi-bin|vendor|xmlrpc\.php)/i,
  /^\/(?:impressum|imprint)\/?$/i,
];

function evaluate(): VisitorVerdict {
  const clean: VisitorVerdict = { is_bot: false, bot_reason: null, bot_confidence: "none" };
  if (typeof window === "undefined" || typeof navigator === "undefined") return clean;

  const nav = navigator as Navigator & {
    webdriver?: boolean;
    hardwareConcurrency?: number;
    languages?: readonly string[];
  };

  // --- Certain signals -----------------------------------------------------
  const ua = nav.userAgent || "";
  if (!ua) return { is_bot: true, bot_reason: "empty_user_agent", bot_confidence: "certain" };

  for (const p of UA_BOT_PATTERNS) {
    if (p.re.test(ua)) return { is_bot: true, bot_reason: p.reason, bot_confidence: "certain" };
  }

  if (nav.webdriver === true) {
    return { is_bot: true, bot_reason: "navigator_webdriver", bot_confidence: "certain" };
  }

  // --- Suspected signals ---------------------------------------------------
  const suspected: string[] = [];

  try {
    const path = window.location.pathname || "/";
    if (SCANNER_PATH_PATTERNS.some((re) => re.test(path))) suspected.push("scanner_path");
  } catch { /* ignore */ }

  try {
    if (window.screen && (window.screen.width === 0 || window.screen.height === 0)) {
      suspected.push("zero_screen");
    }
  } catch { /* ignore */ }

  if (typeof nav.hardwareConcurrency === "number" && nav.hardwareConcurrency === 0) {
    suspected.push("no_cpu_cores");
  }

  if (Array.isArray(nav.languages) ? nav.languages.length === 0 : !nav.language) {
    suspected.push("no_languages");
  }

  try {
    // Documents that were never visible are prerenders / background fetches.
    const doc = document as Document & { prerendering?: boolean };
    if (doc.prerendering === true) suspected.push("prerendering");
    else if (doc.visibilityState === "hidden") suspected.push("hidden_on_load");
  } catch { /* ignore */ }

  if (suspected.length >= 2) {
    return { is_bot: true, bot_reason: suspected.join("+"), bot_confidence: "suspected" };
  }
  if (suspected.length === 1) {
    // One weak signal alone isn't enough to drop the session, but record it.
    return { is_bot: false, bot_reason: suspected[0] ?? null, bot_confidence: "none" };
  }

  return clean;
}

let memo: VisitorVerdict | null = null;

/** Classify the current visitor. Computed once per session and cached. */
export function classifyVisitor(): VisitorVerdict {
  if (memo) return memo;
  if (typeof window === "undefined") {
    return { is_bot: false, bot_reason: null, bot_confidence: "none" };
  }
  try {
    const cached = sessionStorage.getItem(CACHE_KEY);
    if (cached) {
      memo = JSON.parse(cached) as VisitorVerdict;
      return memo;
    }
  } catch { /* ignore */ }

  memo = evaluate();
  try { sessionStorage.setItem(CACHE_KEY, JSON.stringify(memo)); } catch { /* ignore */ }
  return memo;
}

/** Flat metadata fields merged into every analytics event. */
export function visitorClassMetadata(): Record<string, unknown> {
  const v = classifyVisitor();
  return {
    is_bot: v.is_bot,
    bot_reason: v.bot_reason,
    bot_confidence: v.bot_confidence,
  };
}
